// Native share utility — triggers the OS share sheet directly without a UI popup.
// Falls back silently if the Web Share API is not available.
import { APP_NAME } from '@/config/brand';

export interface ShareFileData {
  imageUrl: string;
  fileName?: string;
  prompt?: string;
  title?: string;
}

/**
 * Attempts to share a single image via the native OS share sheet.
 * No UI is shown — the OS-level share panel pops up directly.
 * If not supported, this resolves without doing anything (callers can fall back).
 */
export async function nativeShareImage(data: ShareFileData): Promise<boolean> {
  const { imageUrl, fileName = `nanoimage2_${Date.now()}.jpg`, prompt, title } = data;

  try {
    const response = await fetch(imageUrl, { mode: 'cors' });
    const blob = await response.blob();
    const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });

    const sharePayload: Record<string, unknown> = {
      files: [file],
      title: title || APP_NAME,
      text: prompt || '',
    };

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share(sharePayload as Parameters<typeof navigator.share>[0]);
      return true;
    }
  } catch {
    // Silently fail — callers can fall back to manual download
  }
  return false;
}

/**
 * Downloads an image to the user's device (browser fallback).
 * Tries blob download first, then falls back to direct link.
 */
export async function downloadImage(imageUrl: string, fileName?: string): Promise<boolean> {
  const name = fileName || `nanoimage2_${Date.now()}.jpg`;
  try {
    const response = await fetch(imageUrl, { mode: 'cors' });
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    return true;
  } catch {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  }
}
